import { BlockError } from '@/blocks.04-project/block-error/block-error';
import { LazyInit } from '@/blocks.04-project/lazy-init/lazy-init';
import merge from 'lodash-es/merge';
import toNumber from 'lodash-es/toNumber';
import escape from 'lodash-es/escape';
import get from 'lodash-es/get';

/***
 * Класс кастомного селектора
 * @example
 *
 * Аттрибуты:
 * data-min-query-length {number, default = 0} - минимальная длина слова для начала поиска
 * data-no-result-text {string, default = 'Нет совпадений'} - текст, который выводится если ничего не найдено
 * data-max-options {number, default = 50} - максимальное количество вариантов для вывода в выпадающем списке
 * data-no-highlight {boolean, default = false} - выключение подсветки вхождений поисковой строки в выдаче
 * data-checkboxes {boolean, default = false} - включение режима с чекбоксами
 * data-ajax-url {string, default = ''} - ссылка для запроса данных (если требуется ajax поиск)
 * data-hide-selected {boolean, default = false} - скрывать выбранные элементы из инпута
 * data-no-delete {boolean, default = false} - запрещает удалять выбранный пункт
 * data-icon {'select' | 'world'} - отвечает за показ нужного изображения в селекте
 * data-focus-scroll {boolean, default = false} - отвечает за скролл элемента при открытии клавиатуры на мобилке
 * data-offset-scroll {number, default = 0} - отвечает за отступ для скролла на мобилке
 * data-clear-options {boolean, default = false} - отвечает за очистку данных при повторном запросе
 * data-has-pagination {boolean, default = false} - отвечает за постраничную пагинацию
 * data-disable-filter {boolean, default = false} - отключает фильтр tom-select
 * data-edit-on-backspace {boolean, default = false} - включает редактирование выбранного пункта на backspace
 * data-selected-label-field {string, default = 'text'} - ключ получения отображения выбранных данных
 * data-label-field {string, default = 'text'} - ключ получения отображения данных в выпадающем списке
 * data-value-field {string, default = 'value'} - ключ получения данных для value
 *
 * Аттрибуты на элементах:
 * data-input {boolean, default = false} - инпут, который будет преобразовываться
 * placeholder {string, default = ''} - placeholder у инпута
 * multiple {boolean, default = false} - множественный выбор
 *
 * Классы:
 * custom-select_loaded - вешается после того, как библиотека инициализировалась
 *
 * Пример данных по ajax:
 * {
 *   result: {
 *     items: [
 *       text: '',
 *       value: '',
 *     ]
 *   }
 * }
 *
 * @event iv.custom-select.initialize - испускается после того, как инпут был полностью проинициализирован
 * @event change.iv.customSelect {detail: item} - испускается после того, как был выбран элемент. Если detail: null значение убрано
 */
class CustomSelect {
	/***
	 * Конструктор класса
	 * @param elem {HTMLElement} DOM элемент с классом custom-select
	 * @param force {boolean} принудительно инициализирование
	 */
	constructor(elem, force) {
		this.BlockError = new BlockError({ blockName: 'CustomSelect', destroy: this.destroy });

		if (!elem) return this.BlockError.error('Отсутствует elem');
    this.block = elem;
    this.block.CustomSelect = this;
		this.block.AsyncCustomSelect = new Promise((resolve) => {
			this.promiseResolve = resolve;
		}).then((ins) => ins);

		this.LazyInit = new LazyInit({ ins: this, callback: this.init, addClass: 'custom-select_inited' });
		this.LazyInit.init();
		if (force) this.LazyInit.earlyInit();
  }

  init = async () => {
		this.setOptions();
		await this.initSelect();

		this.input.addEventListener('focus', this.selectFocusHandler);
		this.input.addEventListener('blur', this.selectBlurHandler);
		if (this.form) this.form.addEventListener('reset', this.resetHandler);

		this.block.dispatchEvent(new Event('iv.custom-select.initialize', { bubbles: true }));

		this.promiseResolve(this);
		this.block.AsyncCustomSelect = this;
  };

	initSelect = async () => {
		const { default: TomSelect } = await import(/* webpackChunkName: "TomSelect" */ 'tom-select').catch((err) => {
			throw new Error(err);
		});

		this.TomSelect = TomSelect;
		this.inputIns = new this.TomSelect(this.input, {
			onInitialize: () => {
				this.block.classList.add('custom-select_loaded');
			},
			score: this.disableFilter ? () => {
				return () => {
					return true;
				};
			} : null,
			shouldLoad: (query) => {
				// Ограничение минимального запроса
				const remainLength = this.minQueryLength - query.length;
				if (remainLength > 0) {
					this.inputIns.dropdown.style.display = '';
					this.inputIns.dropdown_content.innerHTML = `<div class="custom-select__more-length">${this.minQueryLengthText(remainLength)}<div>`;
				}
				return query.length >= this.minQueryLength;
			},
			onItemAdd: (value, item) => {
				if (this.editOnBackspace && !this.multiple) {
					this.inputIns.control_input.value = item.textContent;
				}
			},
			onFocus: () => {
				if (this.editOnBackspace && !this.multiple) {
					this.inputIns.control_input.value = this.inputIns.items.length > 0 ? this.inputIns.getItem(this.inputIns.items[0]).textContent : '';
				}
			},
			...this.options
		});

		if (this.editOnBackspace) {
			this.inputIns.control_input.addEventListener('input', this.onInput);
		}

		this.inputIns.on('dropdown_open', () => {
      setTimeout(() => {
        const dropdown = this.block.querySelector('.ts-dropdown');
        if (dropdown) {
          // Добавление скролла
          const dropdownContent = dropdown.querySelector('.ts-dropdown-content');
          dropdownContent.classList.add('custom-scrollbar');
          dropdownContent.classList.remove('custom-scrollbar_inited');
          window.Block.CustomScrollbar.init(this.block, true);
        }
      }, 0);
			this.control = this.block.querySelector('.ts-control');
      if (this.isScrollOnFocus && this.control) this.focusHandler();
    });

		this.inputIns.on('change', (selectedValue) => {
			const selectedOption = get(this.inputIns, ['options', selectedValue], null);
			this.inputIns.control_input.blur(); // Удаление фокуса после выбора элемента из списка
			this.block.dispatchEvent(new CustomEvent('change.iv.customSelect', { bubbles: true, detail: selectedOption }));
		});
	};

	setOptions = () => {
		this.loaderMessage = window.BX?.message('customSelectLoaderMessage') || 'Загрузка...';

		this.input = this.block.querySelector('[data-input]');
		if (!this.input) return this.BlockError.error('Отсутствует data-input');

		this.form = this.block.closest('form');

		this.multiple = this.input.hasAttribute('multiple');

		this.minQueryLength = toNumber(this.block.dataset['minQueryLength']) || 0;
		this.minQueryLengthText = (count) => window.BX?.Loc?.getMessagePlural('customSelectMinQueryLength', count, {'#COUNT#': count}) || `Пожалуйста, введите больше символов (${count})`;
		this.textNoResult = this.block.dataset['noResultText'] || 'Нет совпадений';

		this.disableFilter = this.block.hasAttribute('data-disable-filter');

		this.selectedLabelField = this.block.dataset['selectedLabelField'] || 'text';

		this.options = {
			maxOptions: this.block.dataset['maxOptions'] || 50,
			highlight: !this.block.hasAttribute('data-no-highlight'),
			sortField: [{ field: '$order' }, { field: '$score' }],
			labelField: this.block.dataset['labelField'] || 'text',
			valueField: this.block.dataset['valueField'] || 'value',
			render: {
				item: (data, escape) => {
					const text = escape(get(data, [this.selectedLabelField], '') || get(data, [this.options.labelField], ''));
					return `<div title="${text}">${text}</div>`;
				},
				no_results: () => {
					return `<div class="no-results">${this.textNoResult}</div>`;
				},
				loading: () => {
					return `<div class="custom-select__loader">${this.loaderMessage}</div>`;
				},
			},
		};

		// Чекбоксы вместо обычных ссылок
		this.hasCheckboxes = this.block.hasAttribute('data-checkboxes');
		if (this.hasCheckboxes) {
			merge(this.options, {
				plugins: {
					checkbox_options: {
						checkedClassNames: ['ts-checked'],
						uncheckedClassNames: ['ts-unchecked'],
					}
				},
			});
		}

		this.noDelete = this.block.hasAttribute('data-no-delete');
		if (this.noDelete) {
			merge(this.options, {
				plugins: { no_backspace_delete: {} },
			});
		}

		//TODO Доработать для multiple
		this.editOnBackspace = this.block.hasAttribute('data-edit-on-backspace');

    this.isScrollOnFocus = this.block.hasAttribute('data-focus-scroll');
    if (this.isScrollOnFocus) {
      this.offsetScroll = this.block.dataset['offsetScroll'] || 0;
      this.block.style.setProperty('--offset-scroll', this.offsetScroll + 'px');
    }

		// AJAX запросы
		this.ajaxUrl = this.block.dataset['ajaxUrl'];
		this.isClearOptions = this.block.hasAttribute('data-clear-options');
		this.hasPagination = this.block.hasAttribute('data-has-pagination');
		if (this.ajaxUrl) {
			// pagination
			if (this.hasPagination) {
				merge(this.options, {
					plugins: {
						virtual_scroll: {}
					},
					firstUrl: (query) => {
						this.currentPage = 1;
						return `${this.ajaxUrl}?q=${encodeURIComponent(query)}&page=1`;
					},
					render: {
						loading_more: () => {
							return `<div class="custom-select__loader">${this.loaderMessage}</div>`;
						},
						no_more_results: () => {
							return '';
						},
					}
				});
			}

			merge(this.options, {
				load: (query, callback) => {
					const url = this.hasPagination ? this.inputIns.getUrl(query) : this.ajaxUrl;
					const dropdown = this.inputIns.dropdown_content;
					const scrollPosition = dropdown.scrollTop;
					fetch(url, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({ q: escape(query) })
					}).then(response => response.json())
						.then(json => {
							if (this.isClearOptions) {
								this.inputIns.clearOptions();
								this.inputIns.clearCache();
							}
							const more = get(json, 'result.pagination.more', false);
							if (this.hasPagination && more) {
								this.currentPage += 1;
								const next_url = `${this.ajaxUrl}?q=${encodeURIComponent(query)}&page=${this.currentPage}`;
								this.inputIns.setNextUrl(query, next_url);
							}

							callback(get(json, 'result.items', []));

							// fix скролла наверх
							if (this.hasPagination) {
								setTimeout(() => {
									dropdown.scrollTop = scrollPosition;
								}, 0);
							}
						})
						.catch(() => {
							callback();
						});
				},
			});
		}
	};

	/***
	 * Фокус на поле поиска, если фокус получил оригинальный select
	 */
	selectFocusHandler = () => {
		this.inputIns.focus();
	};

	/***
	 * Blur с поля поиска, если blur получил оригинальный select
	 */
	selectBlurHandler = () => {
		this.inputIns.blur();
	};

	/***
	 * Очистка формы
	 */
	resetHandler = () => {
		this.inputIns.clear();
	};

  focusHandler = () => {
    const isNotMobile = window.matchMedia('(pointer: fine)').matches;

    if (isNotMobile) return;
    this.control.scrollIntoView();
  };

	onInput = (event) => {
		const str = event.target.value;
		if (str.length === 0 && !this.noDelete && !this.multiple) {
			this.inputIns.clear();
		}
	};


	destroy = () => {
		if (this.inputIns) {
			this.inputIns.control_input.removeEventListener('input', this.onInput);
			this.inputIns.destroy();
		}
		if (this.input) {
			this.input.removeEventListener('focus', this.selectFocusHandler);
			this.input.removeEventListener('blur', this.selectBlurHandler);
		}
    if (this.block) {
      this.block.classList.remove('custom-select_inited');
      this.block.classList.remove('custom-select_loaded');
      delete this.block.CustomSelect;
    }
  };

  static init(container = document, force) {
    Array.from(container.querySelectorAll('.custom-select:not(.custom-select_inited)')).forEach((elem) => new CustomSelect(elem, force));
  }
}

document.addEventListener('DOMContentLoaded', () => CustomSelect.init());

// Экспорт;
window.Block.CustomSelect = CustomSelect;
